/**
 * Camada de permissões do frontend.
 *
 * Regra oficial de semântica:
 * - `hasPermission(code)` → EXACT match. Use para AÇÕES (botões, tabs, toggles):
 *   o usuário precisa ter a permissão folha exata (ex.: `commercial:accounts:new`).
 * - `hasPermissionPrefix(code)` → PREFIX match. Use para NAVEGAÇÃO (menus, seções,
 *   módulos): o item aparece se o usuário tiver `code` ou qualquer permissão filha
 *   iniciada por `code:` (ex.: ter `commercial:accounts:new` libera o menu
 *   `commercial:accounts`).
 *
 * As funções puras recebem o array de permissões; o hook `usePermissions` faz o
 * binding com o usuário autenticado via `useAuth`.
 */
import { useCallback } from 'react';
import { useAuth } from '@/context/auth-context';

/**
 * Prefix match: true se `permissions` contém `code` ou qualquer permissão
 * iniciada por `code:`. Use para visibilidade de menus/seções/módulos.
 */
export function hasPermissionPrefix(code: string, permissions: string[]): boolean {
  return permissions.some((p) => p === code || p.startsWith(`${code}:`));
}

export function usePermissions() {
  const { user } = useAuth();

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return user?.permissions?.includes(permission) ?? false;
    },
    [user?.permissions],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      return permissions.some((p) => user?.permissions?.includes(p));
    },
    [user?.permissions],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      return permissions.every((p) => user?.permissions?.includes(p));
    },
    [user?.permissions],
  );

  const checkPermissionPrefix = useCallback(
    (code: string): boolean => hasPermissionPrefix(code, user?.permissions ?? []),
    [user?.permissions],
  );

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasPermissionPrefix: checkPermissionPrefix,
    permissions: user?.permissions ?? [],
  };
}
